import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useToast,
} from "@chakra-ui/react";
import { t } from "i18next";

import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  BseDto,
  ContractTemplateDto,
  ReceivedBseBidDto,
  useCreateContractFromSourcingEventMutation,
  useListContractTemplatesQuery,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../../common/errors/useApiError";
import { SelectorValue } from "../../../../common/input/Selector/SelectorValue";
import { SingleFormSelector } from "../../../../common/input/Selector/SingleFormSelector";
import { ModalSkeleton } from "../../../../common/ModalSkeleton";
import { urls } from "../../../../urls";

interface Props {
  sourcingEvent: BseDto;
  bid: ReceivedBseBidDto;
  onClose: () => void;
}

interface FormValues {
  title: string;
  template: SelectorValue | null;
}

export const CreateContractModal = ({ sourcingEvent, bid, onClose }: Props) => {
  const authState = useLoggedInWithOrgContextState();
  const navigate = useNavigate();
  const toast = useToast();
  const apiErrorDisplayer = useApiError();

  const [createContract, { isLoading: isCreatingContract }] = useCreateContractFromSourcingEventMutation();

  const { data: templatesRes, isLoading: isLoadingTemplates } = useListContractTemplatesQuery({
    organizationId: authState.selectedOrg.id,
    orgId: authState.selectedOrg.id,
  });

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    setValue,
  } = useForm<FormValues>();

  const getTemplateName = (template: ContractTemplateDto): string => {
    if (template.name) {
      return template.name;
    } else if (template.commonType) {
      switch (template.commonType) {
        case "ExpirationDate":
          return t("Contract with expiration date");
      }
    }
    throw new Error("Invalid template");
  };

  const submit = async (values: FormValues) => {
    try {
      const createResult = await createContract({
        createContractFromSourcingEventRequest: {
          eventId: sourcingEvent.id,
          bidId: bid.id,
          title: values.title,
          templateId: values.template?.value ?? undefined,
        },
      }).unwrap();

      setValue("title", "");
      setValue("template", null);

      toast({
        title: t("Contract created successfully"),
        status: "success",
        position: "top-right",
      });

      navigate(urls.contracts.edit.go(createResult.id, sourcingEvent.id).details);
    } catch (error) {
      apiErrorDisplayer.trigger(error instanceof Error ? error : new Error("Unknown error"));
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="lg">
          {t("New contract with")} {bid.owningOrganization.name}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {isLoadingTemplates ? (
            <ModalSkeleton />
          ) : (
            <form id="create-contract-form" onSubmit={handleSubmit(submit)}>
              <FormControl isInvalid={errors.title !== undefined} pt="2.5">
                <FormLabel htmlFor={"title"}>{t("Contract title")}</FormLabel>
                <Input
                  id={"title"}
                  type="title"
                  backgroundColor="smBackground"
                  {...register("title", {
                    required: t("Please provide a title for your contract") ?? "",
                    minLength: {
                      value: 2,
                      message: t("Must be at least 2 characters long"),
                    },
                  })}
                />
                <FormErrorMessage>{errors.title && errors.title.message}</FormErrorMessage>
                <FormHelperText>{t("Please provide a title for your contract")}</FormHelperText>
              </FormControl>
              <Flex pt="4">
                {templatesRes && (
                  <SingleFormSelector
                    options={templatesRes.templates.map((t) => ({
                      label: getTemplateName(t),
                      value: t.id,
                    }))}
                    name={"template"}
                    label={t("Select type")}
                    helperText={t("Select a type") ?? ""}
                    control={control}
                    placeholderText={""}
                    noOptionsProvidedMessage={t("No types provided")}
                    noMatchingOptionsMessage={t("No matching types")}
                    errorMessage={undefined}
                  />
                )}
              </Flex>
              <Flex width="full" justify="end">
                <Button
                  type="submit"
                  form="create-contract-form"
                  colorScheme="teal"
                  isLoading={isCreatingContract}
                  mt="8"
                >
                  {t("Create contract")}
                </Button>
              </Flex>
            </form>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
